// Тема админки: светлая / тёмная / как в системе. Выбор хранится в
// localStorage и отдаётся Vuetify как имя темы — 'system' Vuetify (≥3.7)
// понимает сам: следит за prefers-color-scheme и снимает слушатель со scope.
// Начальная тема резолвится ещё при createVuetify (resolveInitialTheme), а
// фон до загрузки JS ставит инлайн-скрипт в index.html — тот же ключ хранилища.
import { computed, ref, watch, type Ref } from 'vue'
import { useTheme } from 'vuetify'

export type ThemeMode = 'light' | 'dark' | 'system'

/** Ключ localStorage — продублирован в index.html (инлайн-скрипт до JS). */
export const THEME_STORAGE_KEY = 'magnum-cloud-admin-theme'
const MODES: readonly ThemeMode[] = ['light', 'dark', 'system']

function isMode(v: unknown): v is ThemeMode {
  return typeof v === 'string' && (MODES as readonly string[]).includes(v)
}

/** Сохранённый режим; без записи или в приватном окне — «система». */
export function readStoredMode(): ThemeMode {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY)
    return isMode(v) ? v : 'system'
  } catch {
    return 'system'
  }
}

/** Имя темы для createVuetify — до монтирования приложения. */
export function resolveInitialTheme(): ThemeMode {
  return readStoredMode()
}

// Один общий режим на приложение: применяет его installAppTheme() (корневой
// App.vue — живёт и на /login), меню в шапке только читает/меняет.
const mode: Ref<ThemeMode> = ref(readStoredMode())

/** Вызывать один раз из корневого компонента: держит тему Vuetify равной режиму. */
export function installAppTheme(): void {
  const theme = useTheme()
  watch(
    mode,
    (m) => {
      theme.global.name.value = m
    },
    { immediate: true },
  )
}

export function useAppTheme(): {
  mode: Ref<ThemeMode>
  isDark: Ref<boolean>
  setMode: (m: ThemeMode) => void
} {
  const theme = useTheme()

  function setMode(m: ThemeMode): void {
    mode.value = m
    try {
      localStorage.setItem(THEME_STORAGE_KEY, m)
    } catch {
      // приватное окно / запрещённое хранилище — тема живёт до перезагрузки
    }
  }

  const isDark = computed(() => theme.global.current.value.dark)
  return { mode, isDark, setMode }
}
